// InvoiceFromJobs — pick finished work, get an invoice.
//
// The biller's main flow, and the one invoice capability the rebuild did not
// have: /office/invoices/new gave a blank document, so every invoice had to be
// retyped from a job the crew had already described.
//
//   GET  /api/invoices/available-jobs?include_archived=  completed, UNBILLED jobs
//   POST /api/invoices/from-jobs                         { job_ids, client_*, … }
//
// Two things the server does that shape this screen, both read off
// routes/invoices.js rather than assumed:
//
//   · available-jobs excludes jobs already on a real invoice with no work
//     done since (routes/invoices.js unbilledJobSql), which is the double-bill
//     guard; a running job comes back once more work is approved. Archived
//     jobs are hidden unless asked for, because a job auto-archived by payroll
//     can still be unbilled.
//   · from-jobs bills `completed` AND `archived`, which is what the picker
//     offers. It used to accept only `completed` and silently `continue` past
//     anything else — while still marking every submitted job as billed — so
//     ticking "include archived" here made those jobs vanish from the picker
//     unbilled. Corrected server-side 2026-09-13; a request where NOTHING is
//     billable is now refused 400 with a sentence, which `describeError`
//     below puts on screen, rather than writing an empty $0 invoice.
//
// NO MONEY MATH HERE. from-jobs creates each line at unit_price 0 and a
// person prices it on the invoice; the totals are computed server-side.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { post, describeError } from '../../shared/http';
import { useRole } from '../../../hooks/usePermission';
import { mayUse } from '../shared/moneyEndpoints';
import './InvoiceFromJobs.css';
import { DataTable, FwButton, FwField, FwInput, FwSelect, Notice, Skeleton, EmptyState, Pill, Sheet, SectionLabel } from '../../../ui/fw';
import { useOfficeQuery, PageHeader, Toolbar, SearchBox, LoadError, matches, textOr, fmtDate } from '../records/OfficePage';
import { fileHref, isImageFile } from '../../shared/job/mediaKinds';
import { ESTIMATE_STATUS } from '../shared/status';
import { crewSummary } from '../../shared/jobFields';
import { clientKey, groupByClient, clientOptions } from '../shared/jobsByClient';

const SEARCH_FIELDS = ['title', 'client_name', 'property_name', 'worker_name'];

/** Everyone on a job, lead included — the roster arrives as JSON text. */
export function assigneesOf(job) {
  if (!job || !job.assignees_json) return [];
  try {
    const rows = JSON.parse(job.assignees_json);
    return Array.isArray(rows) ? rows.filter((r) => r && r.name) : [];
  } catch (_) {
    return [];
  }
}

/** Rows the filters let through. */
export function filterJobs(jobs, { q, clientId, workerId }) {
  return (jobs || []).filter((j) => {
    if (clientId && String(j.client_id) !== String(clientId)) return false;
    if (workerId) {
      const onIt = String(j.worker_id) === String(workerId)
        || assigneesOf(j).some((a) => String(a.id) === String(workerId));
      if (!onIt) return false;
    }
    return matches(j, SEARCH_FIELDS, q);
  });
}

// ── Per client ────────────────────────────────────────────────────────────
//
// The owner, 2026-09-25: "When creating invoice, I can NOT sort the jobs per
// client". The server answers finished-first, newest-first
// (routes/invoices.js /available-jobs orderClause), so one client's jobs came
// back scattered among everyone else's — and the client dropdown listed names
// in that same scattered order. An invoice bills ONE client, so the list is
// grouped by client, A–Z, each group with a way to pick all of its jobs.
// "Newest first" keeps the old flat order for a biller who wants it.

export const SORTS = [['client', 'By client'], ['newest', 'Newest first']];

// The grouping itself lives in shared/jobsByClient.js, shared with the
// invoice editor's Link jobs; re-exported so this screen's tests read it here.
export { groupByClient, clientOptions };

/**
 * "Pick all" on one client's group, over `jobs` — the WHOLE list, not just the
 * rows a search left on screen, so a pick the search hid is still known to
 * belong to someone.
 *
 *   Pick all    that client's picks stay (hidden ones included) and the
 *               group's jobs join them. Another client's picks go: one
 *               invoice bills one client, and keeping them would only turn
 *               the button into the "One invoice, one client" refusal.
 *   Unpick all  every pick for that client goes — hidden ones too, so
 *               nothing is left on the invoice with no tick on screen.
 *
 * `all` is judged on the group as shown: every visible job already picked.
 */
export function pickGroup(picked, group, jobs) {
  const ids = group.jobs.map((j) => j.id);
  const all = ids.length > 0 && ids.every((id) => picked.has(id));
  const clientOf = new Map((jobs || group.jobs).map((j) => [j.id, clientKey(j)]));
  const sameClient = (id) => clientOf.get(id) === group.key;
  if (all) return new Set([...picked].filter((id) => !sameClient(id)));
  return new Set([...[...picked].filter(sameClient), ...ids]);
}

/**
 * What a group's button says, or null for no button. `clientTotal` is how many
 * jobs that client has in the WHOLE list.
 *
 * No button on "No client" (those jobs share no client, and one tap would put
 * all of them on a bill to "Client"), nor for a client with one job (its
 * checkbox is the control). The number is what the tap ADDS: with every one of
 * the client's jobs on screen that is all of them; once a search or filter
 * hides some it is only the ones shown, and "Pick all 1" on a client with two
 * would be a count on a billing screen that is not what it does (#710 review).
 */
export function groupButton(group, picked, clientTotal) {
  if (group.key === 'none' || !(clientTotal > 1)) return null;
  if (group.jobs.every((j) => picked.has(j.id))) return 'Unpick all';
  return group.jobs.length === clientTotal ? `Pick all ${clientTotal}` : `Pick the ${group.jobs.length} shown`;
}

/**
 * The header's count. "3 of 2 picked" was possible once a search hid picks
 * (and Pick all keeps them on purpose), so hidden picks are said out loud —
 * they are on the invoice whether or not they are on screen.
 */
export function pickedMeta(selected, rows) {
  const shown = new Set((rows || []).map((j) => j.id));
  const hidden = (selected || []).filter((j) => !shown.has(j.id)).length;
  return hidden
    ? `${selected.length} picked (${hidden} hidden by the search or filters)`
    : `${selected.length} of ${(rows || []).length} picked`;
}

/**
 * One invoice bills ONE client, so a mixed selection has to be refused before
 * it reaches the server — from-jobs takes a single client_name and would
 * quietly put another client's work on the bill.
 */
export function clientConflict(selected) {
  const ids = [...new Set(selected.map((j) => (j.client_id == null ? 'none' : String(j.client_id))))];
  if (ids.length <= 1) return null;
  const names = [...new Set(selected.map((j) => j.client_name || 'No client'))];
  return `Those jobs belong to different clients (${names.join(', ')}). An invoice bills one client, so pick jobs for one of them.`;
}

/**
 * The estimate this job is already quoted on — docs/PARITY_2026-09-08.md §19.
 *
 * `available-jobs` has selected `estimate_number`, `estimate_token` and
 * `estimate_status` per job since #280 and no screen read one. The route says
 * in its own comment (server/routes/invoices.js:195-206) why they are there:
 *
 *   "a job on an accepted estimate looks identical to walk-up work and someone
 *    can invoice it ad-hoc without realising there is a quote they should
 *    convert instead. The token lets the UI link straight to the estimate
 *    document."
 *
 * So this is a CAUTION on the picker, not a decoration: nothing is disabled and
 * nothing is hidden — #280 deliberately stopped hiding quoted jobs, because an
 * estimate is not a bill — but the biller can now see the quote and open it.
 *
 * `estimate_number` is the gate. Most finished work is not quoted, so a helper
 * that answered truthy for an unquoted job would put "Quoted" on every line,
 * and a warning on everything is a warning on nothing.
 *
 * The token addresses the PUBLIC document (`/estimate/:token` → PublicEstimate,
 * App.js), which is the same page the client was sent. A row with no token
 * still reports that a quote exists and simply cannot be opened — knowing is
 * the greater half.
 *
 * READ ONLY. Nothing here touches a price: the estimate's total is not read,
 * not shown, and not compared with anything.
 */
export function quotedOn(job) {
  if (!job || !job.estimate_number) return null;
  const key = job.estimate_status || 'draft';
  const known = ESTIMATE_STATUS[key];
  return {
    number: job.estimate_number,
    status: key,
    // Fall through to the raw key rather than dropping an unknown state: a
    // status this screen has not heard of is still information.
    label: known ? known.label : key,
    href: job.estimate_token ? `/estimate/${job.estimate_token}` : null,
  };
}

const CLOSED_STATUSES = ['completed', 'archived', 'cancelled'];

/**
 * A job that is still running, offered because it has hours on an APPROVED
 * timesheet (routes/invoices.js `billableJobSql`, the #691 consequence).
 *
 * It has to LOOK open. Review on #697: without this it rendered with no pill,
 * "Finished: Not recorded" and a caption calling it finished — while the
 * approval screen told the approver the same job "can take next week's hours".
 */
export function isStillOpen(job) {
  // A row with no status is not claimed to be open: say only what is known.
  // And CANCELLED is not open either (a legacy token jobFields.js knows as a
  // stop): nobody adds hours to it, and "mark it finished first" would be
  // the wrong advice about it (#697 review).
  return !!job && !!job.status && !CLOSED_STATUSES.includes(job.status);
}

/**
 * What billing a still-open job does, said BEFORE Create invoice is pressed.
 *
 * It used to warn that the job would never be offered here again. That was
 * the #697 decision left to the owner, who said "do what makes the most
 * sense": billing is per job, so a running job is billed for the work so far
 * and comes BACK to this picker when more work is approved
 * (routes/invoices.js unbilledJobSql). The notice now says that — it is
 * information, not a warning.
 */
export function stillOpenWarning(selected) {
  const open = (selected || []).filter(isStillOpen);
  if (!open.length) return null;
  const one = open.length === 1;
  return `${one ? '1 of these jobs is' : `${open.length} of these jobs are`} still open. This invoice bills `
    + `the work done so far; ${one ? 'it comes' : 'they come'} back here when more work is approved. `
    + `If the work is finished, mark ${one ? 'it' : 'them'} finished too.`;
}

/**
 * A job this picker is offering AGAIN: it was billed before, and approved
 * work has been done since the day it was billed. Null for a job never billed.
 *
 * Shown on the row so a second bill reads as the follow-up it is — which
 * invoice last billed it, and how much work has been done since — and never as
 * an accidental double bill.
 */
export function billedBefore(job) {
  if (!job || !job.last_invoice_number) return null;
  const hours = Number(job.hours_since_billed);
  // The DAY it was billed is named because it is the boundary: hours worked
  // later on that same day count as billed and are not in the figure
  // (routes/invoices.js workedSinceBilledSql). "After Sep 24" says that;
  // "since" did not (review, #699).
  const day = job.last_billed_on ? fmtDate(job.last_billed_on) : null;
  return {
    invoice: String(job.last_invoice_number),
    hours: Number.isFinite(hours) ? hours : 0,
    label: `Billed on ${job.last_invoice_number}`
      + (Number.isFinite(hours) && hours > 0
        ? ` · ${Math.round(hours * 100) / 100} h worked ${day ? `after ${day}` : 'since'}` : ''),
  };
}

/**
 * What "Create invoice" is about to do, in a sentence, before it is pressed.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * The owner's complaint about screens of this shape is literally "what am I
 * approving, how can I tell what is what?". This one had the negative half
 * only: pick jobs for two clients and `clientConflict` explains the refusal,
 * but pick jobs for ONE client and the screen says nothing at all about the
 * invoice that is about to exist. Measured at 320px, the whole of what it
 * said was the header meta — "3 of 8 picked" — and a Create invoice button
 * 444px above the checkboxes that enabled it.
 *
 * Three things are not obvious from the picker and all three are decided the
 * moment that button is pressed, so they are named here:
 *
 *   WHO is billed. fromJobsBody sends `selected[0].client_name` — the client
 *     of the FIRST picked job — for every job in the selection. That is safe
 *     only because clientConflict refuses a mixed one, and a biller cannot see
 *     either fact from the row they happen to have ticked.
 *   WHAT state it lands in. A DRAFT. Nothing is sent to anybody by this
 *     button.
 *   WHAT the lines are worth. Nothing yet: routes/invoices.js creates every
 *     line at unit_price 0 and a person prices them on the invoice.
 *
 * NO ARITHMETIC, and no figure at all. "no price on it" describes the
 * unit_price 0 the ROUTE writes; it is not a total, and nothing on this screen
 * reads, adds or renders a money column. The test holds that line by refusing
 * any "$<digit>" in the sentence.
 */
export function creationSummary(selected) {
  if (!selected || !selected.length) return null;
  if (clientConflict(selected)) return null;
  const client = selected[0].client_name || 'this client';
  const n = selected.length;
  return `Creates one DRAFT invoice for ${client} from ${n} finished job${n === 1 ? '' : 's'}. `
    + 'Every line starts with no price on it — you set the prices on the invoice, and nothing is '
    + 'sent to the client until you send it.';
}

/** The body POST /invoices/from-jobs expects. */
export function fromJobsBody(selected, { includePhotos, notes }) {
  const first = selected[0] || {};
  return {
    job_ids: selected.map((j) => j.id),
    client_id: first.client_id ?? null,
    client_name: first.client_name || '',
    include_photos: !!includePhotos,
    notes: notes || '',
  };
}

/**
 * What the crew actually did on one job, before it goes on a bill (§20).
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * docs/PARITY_2026-09-08.md §20. The classic Billing.js called
 * GET /api/invoices/job-details/:jobId before attaching a job.
 * server/routes/invoices.js:262 still serves it — the job row, the worker's
 * name, the subtasks and the image photos — and had no caller. This screen
 * showed subtask and photo COUNTS off available-jobs, which say how much work
 * there was and nothing about what it was.
 *
 * ── What is deliberately NOT on it ─────────────────────────────────────────
 *
 * `pay_amount` and `pay_type` are on the wire, because job-details selects
 * `j.*`. They are what the CREW is paid for the job, not what the CLIENT is
 * billed for it, and this is the screen an invoice is assembled on. A crew
 * pay figure rendered one column from an invoice total is a plausible number
 * somebody would price a line from — the exact failure CLAUDE.md calls the
 * worst outcome available here. Neither is drawn, and
 * InvoiceJobPreview.test.js fails if either ever is.
 *
 * `total_time` and `estimated_time` are left out for the same reason one step
 * removed: on a billing screen an hours figure reads as billable hours.
 *
 * Nothing here computes anything. Every value is a stored column rendered as
 * it arrived. CLAUDE.md rule 4.
 */
export function JobPreview({ job, onClose }) {
  const q = useOfficeQuery(['invoice-job-details', job.id], `/invoices/job-details/${job.id}`);
  const d = q.data || null;
  const subtasks = Array.isArray(d && d.subtasks) ? d.subtasks : [];
  // The route already filters to file_type 'image', but a preview that
  // <img>s whatever it is handed renders a broken glyph for a PDF the day
  // that changes. Ask the shared accessor instead of trusting the query.
  const photos = (Array.isArray(d && d.photos) ? d.photos : []).filter(isImageFile);

  return (
    <Sheet open title={job.title || `Job #${job.id}`} onClose={onClose}>
      <div className="of-preview">
        {q.isLoading ? <Skeleton lines={5} /> : null}
        {/* Names what failed FIRST, then whatever the server said.
            describeError only reaches its fallback when the server sent
            nothing usable, so a 500 whose body is one word would otherwise
            render as that word alone — and "boom" does not tell a biller that
            the job's work list is MISSING rather than empty, on the screen
            where the next action is billing a client for it. */}
        {q.error ? (
          <Notice tone="stop">
            {`Could not load this job. ${describeError(q.error)}`}
          </Notice>
        ) : null}
        {d ? (
          <>
            <div className="of-preview__who">
              {textOr(d.worker_name)}
              {d.completed_at ? ` · finished ${fmtDate(d.completed_at)}` : ''}
            </div>
            {d.description ? (
              <p className="of-preview__desc">{d.description}</p>
            ) : (
              <p className="of-preview__desc of-preview__empty">No description on this job.</p>
            )}

            <SectionLabel>Work list</SectionLabel>
            {subtasks.length === 0 ? (
              /* Named, never blank: "nothing was recorded" and "we could not
                 tell you what was recorded" must not look the same on the
                 screen where the next action is billing a client. */
              <p className="of-preview__empty">No work list on this job.</p>
            ) : (
              <ul className="of-preview__tasks">
                {subtasks.map((s) => (
                  <li
                    key={s.id}
                    data-preview-subtask={String(s.id)}
                    data-done={s.completed ? 'true' : 'false'}
                    className="of-preview__task"
                  >
                    <Pill tone={s.completed ? 'ok' : 'neutral'}>{s.completed ? 'Done' : 'Not done'}</Pill>
                    <span className="of-preview__task-text">
                      {s.title}
                      {s.description ? <span className="of-preview__task-note">{s.description}</span> : null}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <SectionLabel>Photos</SectionLabel>
            {photos.length === 0 ? (
              <p className="of-preview__empty">No photos on this job.</p>
            ) : (
              <div className="of-preview__photos">
                {photos.map((p) => (
                  <figure key={p.id} data-preview-photo={String(p.id)} className="of-preview__photo">
                    <img src={fileHref(p)} alt={p.caption || p.original_name || 'Job photo'} loading="lazy" />
                    {p.caption ? <figcaption>{p.caption}</figcaption> : null}
                  </figure>
                ))}
              </div>
            )}
          </>
        ) : null}
      </div>
    </Sheet>
  );
}

export function jobColumns({ picked, toggle, onPreview }) {
    return [
      {
        key: 'pick',
        label: '',
        // 'badge' puts the checkbox beside the title on a card, which is where a
        // selection control belongs. Without a role it defaulted to 'fact', and
        // planCard promotes the first VISIBLE column to the title — so on a phone
        // the headline of every job card was a bare checkbox, the job title fell
        // to a truncated fact, and Crew / Finished / On the job were pushed past
        // RECORD_FACT_LIMIT and off the card entirely. Desktop is a table and was
        // fine, which is why it went unseen.
        role: 'badge',
        render: (j) => (
          <input
            type="checkbox"
            checked={picked.has(j.id)}
            onChange={() => toggle(j)}
            aria-label={`Include ${j.title}`}
          />
        ),
      },
      { key: 'title', label: 'Job', role: 'title' },
      { key: 'client_name', label: 'Client', role: 'subtitle', render: (j) => textOr(j.client_name) },
      // Off the card, not off the table. It is blank on most jobs, and a fact
      // reading "—" spends one of three slots saying nothing.
      { key: 'property_name', label: 'Property', role: 'hidden', render: (j) => textOr(j.property_name) },
      // The whole crew, and "Unassigned" in words when there is none.
      //
      // This was `textOr(j.worker_name)`, which is the LEAD alone — so a job
      // three people worked billed as one name, and a job nobody was formally
      // assigned showed an em dash where a person goes. Both matter here:
      // the biller is deciding what a client is charged for, and "who did
      // this" is the question the line item answers. `assigneesOf` parses the
      // roster this endpoint already sends as `assignees_json` and the filter
      // above already reads; `worker_name` stays as the fallback for a job
      // with a lead pointer and no roster row, and `crewSummary` is what
      // says "Unassigned" rather than leaving the slot blank.
      { key: 'worker_name', label: 'Crew', render: (j) => crewSummary({ assignees: assigneesOf(j), user_name: j.worker_name }) },
      // And the same rule for the slot beside it. A card has three fact slots
      // and this is one of them, so an em dash here is a third of everything
      // the card had left to say. It is not filler: available-jobs also serves
      // ARCHIVED jobs, which may never have been completed at all
      // (routes/invoices.js orders "completed first, then archived"), so the
      // absence is a real fact about the job and says so.
      { key: 'completed_at', label: 'Finished', render: (j) => {
        if (j.completed_at) return fmtDate(j.completed_at);
        return isStillOpen(j) ? 'Still open' : 'Not recorded';
      } },
      {
        key: 'extras',
        label: 'On the job',
        // BOTH the preview and the quote flag ride in THIS cell, and that is
        // not tidiness. Crew / Finished / On the job is exactly
        // RECORD_FACT_LIMIT, and planCard drops anything past it — so either
        // of these as a fourth column would push "On the job" off every phone
        // card, which is the defect this screen already shipped once. The
        // counts, the way to see what is behind them, and the warning that
        // this work is already quoted belong together anyway: they are all
        // "what you should know before billing this".
        render: (j) => {
          const quote = quotedOn(j);
          // ONE wrapping row, not a bare fragment. The card's fact value is
          // `nowrap` + ellipsis (ui/fw RecordList), so a fragment of pills
          // that did not fit collapsed the WHOLE cell to "…" — measured at
          // 320px when the billed-before note was added: "Still open", the
          // note and Preview all vanished. The wrapper re-enables wrapping
          // for this cell only; RecordList is shared and stays as it is.
          return (
            <span className="of-extras">
              {j.subtask_count ? <Pill tone="neutral">{j.subtasks_completed || 0}/{j.subtask_count} done</Pill> : null}
              {j.photo_count ? <Pill tone="neutral">{j.photo_count} photo{j.photo_count === 1 ? '' : 's'}</Pill> : null}
              {/* Put away by PAYROLL (approval files completed jobs away,
                  naming the sheet) is finished work waiting to be billed —
                  it is on the default list and says so. Put away by a
                  person is the one worth a second look. */}
              {j.status === 'archived' && j.archived_timesheet_id != null
                ? <Pill tone="neutral" data-processed>Payroll processed</Pill>
                : j.status === 'archived' ? <Pill tone="warn">Archived</Pill> : null}
              {isStillOpen(j) ? <Pill tone="warn" data-still-open>Still open</Pill> : null}
              {billedBefore(j) ? (
                <span className="of-extras__note" data-billed-before={billedBefore(j).invoice}>{billedBefore(j).label}</span>
              ) : null}
              {/* `warn` at every estimate state rather than the estimate's own
                  tone: on THIS screen the point is "stop and look", and an
                  accepted quote — the one that should be converted rather than
                  billed ad-hoc — would otherwise come out green. The state is
                  in the words, so the colour is not carrying it alone. */}
              {quote && quote.href ? (
                <a className="of-link" href={quote.href} target="_blank" rel="noreferrer" data-quoted={quote.number}>
                  <Pill tone="warn">Quoted {quote.number} · {quote.label}</Pill>
                </a>
              ) : null}
              {quote && !quote.href ? (
                <Pill tone="warn" data-quoted={quote.number}>Quoted {quote.number} · {quote.label}</Pill>
              ) : null}
              {onPreview ? (
                <FwButton
                  variant="quiet"
                  data-preview-job={String(j.id)}
                  aria-label={`Preview ${j.title}`}
                  onClick={() => onPreview(j)}
                >
                  Preview
                </FwButton>
              ) : null}
            </span>
          );
        },
      },
    ];
}

/**
 * The columns, and the ROLE each plays on a card.
 *
 * Exported so planCard's answer can be asserted directly. Every column here
 * used to be role-less, and planCard promotes the first VISIBLE column to the
 * title — which on a phone made the headline of every job card a bare
 * checkbox. The job title dropped to a truncated fact, and Crew / Finished /
 * On the job were pushed past RECORD_FACT_LIMIT and off the card entirely, so
 * a card showed a tick box, "JOB / Billable B phone 10…", and "PROPERTY —".
 *
 * Desktop renders a table and was correct throughout, which is why this
 * survived: it is only wrong in the shape nobody had populated. The ui-check
 * scenario that covers this screen had never once run with a job in it — it
 * looked for `tbody input[type=checkbox]`, found none below 768px because
 * RecordList draws cards there, and took its "nothing to pick" exit as a PASS.
 */
export default function InvoiceFromJobs() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // POST /invoices/from-jobs is checkRole('admin','manager'), so the gate is
  // the role — useCan('invoices','create') is true for accounting, whom every
  // route in invoices.js refuses (moneyEndpoints.js header).
  const canCreate = mayUse('invoiceFromJobs', useRole());

  const [includeArchived, setIncludeArchived] = useState(false);
  const [q, setQ] = useState('');
  const [clientId, setClientId] = useState('');
  const [sort, setSort] = useState('client');
  const [workerId, setWorkerId] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [includePhotos, setIncludePhotos] = useState(true);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // The job being looked at, which is NOT the job being picked. Previewing
  // must never select — a biller who opens a job to look at it and finds it
  // on the invoice has been handed a trap.
  const [previewing, setPreviewing] = useState(null);

  const jobsQ = useOfficeQuery(
    ['available-jobs', includeArchived],
    `/invoices/available-jobs${includeArchived ? '?include_archived=true' : ''}`,
  );

  const jobs = useMemo(() => (Array.isArray(jobsQ.data) ? jobsQ.data : []), [jobsQ.data]);
  const rows = useMemo(() => filterJobs(jobs, { q, clientId, workerId }), [jobs, q, clientId, workerId]);
  const selected = useMemo(() => jobs.filter((j) => picked.has(j.id)), [jobs, picked]);

  const clients = useMemo(() => clientOptions(jobs), [jobs]);
  // How many jobs each client has in the WHOLE list, so a search that leaves
  // one of a client's jobs on screen cannot hide the button that lets go of
  // the others it picked (#710 review).
  const perClient = useMemo(() => {
    const n = new Map();
    for (const j of jobs) { const k = j.client_id == null ? 'none' : String(j.client_id); n.set(k, (n.get(k) || 0) + 1); }
    return n;
  }, [jobs]);
  const groups = useMemo(() => (sort === 'client' ? groupByClient(rows) : null), [rows, sort]);

  const workers = useMemo(() => {
    const seen = new Map();
    for (const j of jobs) {
      if (j.worker_id && !seen.has(j.worker_id)) seen.set(j.worker_id, j.worker_name);
      for (const a of assigneesOf(j)) if (!seen.has(a.id)) seen.set(a.id, a.name);
    }
    return [...seen].map(([id, name]) => ({ id, name })).filter((w) => w.name);
  }, [jobs]);

  const conflict = clientConflict(selected);
  const summary = creationSummary(selected);
  const openWarning = stillOpenWarning(selected);

  const toggle = (job) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(job.id)) next.delete(job.id); else next.add(job.id);
    return next;
  });

  const create = async () => {
    if (!selected.length || conflict) return;
    setBusy(true);
    setError('');
    try {
      const made = await post('/invoices/from-jobs', fromJobsBody(selected, { includePhotos, notes }));
      await qc.invalidateQueries({ queryKey: ['invoices'] });
      await qc.invalidateQueries({ queryKey: ['available-jobs'] });
      navigate(made && made.id ? `/invoices/${made.id}` : '/invoices');
    } catch (e) {
      setError(describeError(e, 'Could not create the invoice.'));
      // Already billed (409): the server refused jobs this list still shows,
      // usually because another tab billed them. Refresh the list and let go
      // of those jobs, so the screen stops offering what it just refused
      // (review, #699). The sentence above says which ones and why.
      if (e && e.status === 409) {
        const refused = new Set(((e.body && e.body.jobs) || []).map((j) => j && j.id));
        if (refused.size) {
          setPicked((prev) => new Set([...prev].filter((id) => !refused.has(id))));
        }
        qc.invalidateQueries({ queryKey: ['available-jobs'] });
      }
    } finally {
      setBusy(false);
    }
  };

  const COLUMNS = jobColumns({ picked, toggle, onPreview: setPreviewing });

  if (jobsQ.isLoading) return <Skeleton lines={8} />;
  if (jobsQ.error) return <LoadError error={jobsQ.error} fallback="Could not load the finished jobs." />;

  return (
    <>
      <PageHeader title="Invoice from jobs" meta={pickedMeta(selected, rows)}>
        <FwButton variant="ghost" onClick={() => navigate('/invoices')}>Cancel</FwButton>
        {canCreate ? (
          <FwButton variant="primary" icon="billing" onClick={create} loading={busy} disabled={!selected.length || !!conflict}>
            Create invoice
          </FwButton>
        ) : null}
      </PageHeader>

      {error ? <Notice tone="stop" title="Not created">{error}</Notice> : null}
      {conflict ? <Notice tone="warn" title="One invoice, one client">{conflict}</Notice> : null}
      {/* The positive half of the same question the conflict notice answers.
          It names the client the bill goes to, which is decided by the FIRST
          job in the selection and is not visible from the rows. */}
      {summary ? <Notice tone="info" title="What this will create" data-create-summary>{summary}</Notice> : null}
      {openWarning ? <Notice tone="info" title="Still open" data-still-open-warning>{openWarning}</Notice> : null}

      <Toolbar>
        <SearchBox value={q} onChange={setQ} placeholder="Search jobs, clients, crew" />
        <FwSelect aria-label="Filter by client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Any client</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </FwSelect>
        <FwSelect aria-label="Filter by crew" value={workerId} onChange={(e) => setWorkerId(e.target.value)}>
          <option value="">Anyone</option>
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </FwSelect>
        <FwSelect aria-label="Sort jobs" value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORTS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
        </FwSelect>
        <label className="or-form__check">
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Include archived
        </label>
      </Toolbar>

      {rows.length && groups ? (
        groups.map((g) => {
          const button = groupButton(g, picked, perClient.get(g.key) || 0);
          return (
            <section key={g.key} className="of-client-group" data-client-group={g.name} data-client-key={g.key}>
              <div className="of-client-group__head">
                <SectionLabel as="h2" className="of-client-group__name">{g.name}</SectionLabel>
                <span className="of-client-group__count">{g.jobs.length} job{g.jobs.length === 1 ? '' : 's'}</span>
                {/* groupButton says when there is one, and why not on "No
                    client" or a one-job client (#710 review). */}
                {button ? (
                  <FwButton variant="ghost" onClick={() => setPicked((prev) => pickGroup(prev, g, jobs))}>
                    {button}
                  </FwButton>
                ) : null}
              </div>
              <DataTable
                columns={COLUMNS}
                rows={g.jobs}
                caption={`${g.name} — ${includeArchived ? 'jobs' : 'finished jobs'} that have not been billed`}
              />
            </section>
          );
        })
      ) : rows.length ? (
        <DataTable
          columns={COLUMNS}
          rows={rows}
          caption={includeArchived ? 'Jobs that have not been billed' : 'Finished jobs that have not been billed'}
        />
      ) : (
        <EmptyState title={jobs.length ? 'Nothing matches those filters' : 'No unbilled finished jobs'}>
          {jobs.length
            ? 'Clear the search, or try including archived jobs.'
            : 'Completed jobs, and jobs whose payroll has run, appear here until they are on an invoice.'}
        </EmptyState>
      )}

      <div className="or-actions">
        <label className="or-form__check">
          <input type="checkbox" checked={includePhotos} onChange={(e) => setIncludePhotos(e.target.checked)} />
          Attach the crew's photos
        </label>
      </div>
      <FwField label="Notes on the invoice" htmlFor="ifj-notes">
        <FwInput id="ifj-notes" value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
      </FwField>

      {previewing ? <JobPreview job={previewing} onClose={() => setPreviewing(null)} /> : null}
    </>
  );
}
